/**
 * Was über eine Datei ohne jede Analyse gesagt werden kann: wie groß, welcher Art, wann zuletzt
 * angefasst.
 *
 * Herausgelöst, weil es an zwei Stellen gebraucht wird — in der Dateiverwaltung und im Datenraum —
 * und weil die Zahlen sonst zweimal formatiert würden, mit zwei Rundungen und zwei Einheiten.
 */

const UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

/**
 * Eine Größe in der Einheit, in der ein Mensch sie liest.
 *
 * Kein Wert für eine Größe, die es nicht gibt: `undefined` heißt „unbekannt" und wird oben
 * weggelassen, statt als „0 Bytes" zu erscheinen — eine Datei von null Bytes und eine Datei, deren
 * Größe die Ablage nicht mitgeteilt hat, sind zwei verschiedene Dinge.
 */
export const formatFileSize = (bytes: unknown): string | undefined => {
    // Erst prüfen, dann umwandeln. `Number(null)` und `Number('')` sind beide 0 — eine Größe, die
    // die Ablage nicht gemeldet hat, käme sonst als „0 Bytes" heraus und läse sich wie eine leere
    // Datei.
    const value =
        typeof bytes === 'number'
            ? bytes
            : typeof bytes === 'string' && bytes.trim() !== ''
              ? Number(bytes)
              : NaN;

    if (!Number.isFinite(value) || value < 0) return undefined;
    if (value === 0) return `0 ${UNITS[0]}`;

    const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), UNITS.length - 1);
    const scaled = value / 1024 ** exponent;

    // Zwei Nachkommastellen, aber keine überflüssigen: „1,5 MB" statt „1,50 MB".
    return `${parseFloat(scaled.toFixed(2))} ${UNITS[exponent]}`;
};

/**
 * Die Art der Datei, wie man sie nennt — nicht ihr MIME-Typ.
 *
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document` sagt einem Leser
 * nichts; „Word-Dokument" schon. Abgeleitet aus der Endung, weil die Ablage keinen MIME-Typ führt
 * und die Endung ohnehin das ist, wonach hier alles andere auch geht.
 */
const KINDS: Record<string, string> = {
    pdf: 'PDF',
    doc: 'Word-Dokument',
    docx: 'Word-Dokument',
    xls: 'Excel-Tabelle',
    xlsx: 'Excel-Tabelle',
    csv: 'Tabelle (CSV)',
    ppt: 'Präsentation',
    pptx: 'Präsentation',
    txt: 'Textdatei',
    md: 'Markdown',
    json: 'JSON',
    xml: 'XML',
    zip: 'Archiv',
    png: 'Bild (PNG)',
    jpg: 'Bild (JPEG)',
    jpeg: 'Bild (JPEG)',
    gif: 'Bild (GIF)',
    svg: 'Bild (SVG)',
    tif: 'Bild (TIFF)',
    tiff: 'Bild (TIFF)',
    dwg: 'CAD-Zeichnung',
    dxf: 'CAD-Zeichnung',
};

export const fileKind = (name: unknown): string | undefined => {
    const text = typeof name === 'string' ? name : '';
    const dot = text.lastIndexOf('.');
    const extension = dot === -1 ? undefined : text.slice(dot + 1).toLowerCase();

    if (!extension) return undefined;

    // Unbekannte Endung: die Endung selbst, in Großbuchstaben. Besser als „Datei" — sie sagt
    // wenigstens, womit man es zu tun hat.
    return KINDS[extension] ?? extension.toUpperCase();
};

/** Ein Zeitpunkt, wie ihn die eingestellte Sprache schreibt; nichts, wo keiner steht. */
export const formatFileDate = (value: unknown, locale: string): string | undefined => {
    if (!value) return undefined;

    const date = new Date(value as any);

    return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString(locale);
};

/**
 * Ein Text, gekürzt auf eine lesbare Länge.
 *
 * Für Stellen, an denen ein Fließtext nur andeuten soll, worum es geht — ein Kurzhinweis am
 * Mauszeiger etwa. Gekürzt wird auf ganze Wörter: Ein Schnitt mitten im Wort liest sich wie ein
 * Fehler, und die paar Zeichen, die er gewinnt, braucht niemand.
 *
 * Zurückgegeben wird der Text unverändert, wo er ohnehin kurz genug ist — dann gehört auch kein
 * Auslassungszeichen dahinter, das ein Weiterlesen verspricht, das es nicht gibt.
 */
export const truncateText = (value: unknown, max: number): string | null => {
    if (typeof value !== 'string') return null;

    const text = value.trim().replace(/\s+/g, ' ');
    if (!text) return null;
    if (text.length <= max || max <= 0) return text;

    const cut = text.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');

    // Nur zurück bis zum Wortanfang, und nur wenn dabei nicht der halbe Text verlorengeht — ein
    // einziges sehr langes Wort wird hart geschnitten, sonst bliebe fast nichts stehen.
    const head = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;

    return `${head.replace(/[\s.,;:]+$/, '')}…`;
};
